import { FC, useMemo, useState } from "react";
import { useTable, useReducer } from "spacetimedb/react";
import { tables, reducers } from "@module-bindings";
import { formatAssetLabel, formatAssetValue } from "@/lib/marketUtils";

export const AdminPage: FC = () => {
  const [appConfig] = useTable(tables.app_config);
  const [accounts] = useTable(tables.accounts);
  const [ledgerParams] = useTable(tables.ledger_parameters);
  const [tpsRows] = useTable(tables.tps_stats);

  const setTradingEnabled = useReducer(reducers.setTradingEnabled);
  const [toggling, setToggling] = useState(false);

  const tradingEnabled = appConfig.length > 0 ? !!appConfig[0].tradingEnabled : false;

  const feeAccountId = ledgerParams.length > 0
    ? ledgerParams[0].feeAccountPublicKeyHash
    : null;

  const feeAccount = useMemo(() => {
    if (feeAccountId == null) return null;
    return accounts.find((a) => a.accountId === feeAccountId) ?? null;
  }, [accounts, feeAccountId]);

  const feeBalances = useMemo(() => {
    if (!feeAccount) return [];
    return feeAccount.uncommittedValue.assets
      .filter((a: any) => a.value > 0n)
      .map((a: any) => ({
        label: formatAssetLabel(a.asset),
        formatted: formatAssetValue(a.asset, a.value),
        raw: a.value,
      }));
  }, [feeAccount]);

  const tps = tpsRows.length > 0 ? (tpsRows[0] as any).lastSecondCount ?? 0 : 0;
  const totalEvents = tpsRows.length > 0 ? Number((tpsRows[0] as any).totalEvents ?? 0n) : 0;

  const handleToggle = async () => {
    setToggling(true);
    try {
      setTradingEnabled(!tradingEnabled);
    } catch (err) {
      console.error("Failed to toggle trading:", err);
    } finally {
      setTimeout(() => setToggling(false), 1000);
    }
  };

  return (
    <main className="flex-1 flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-lg flex flex-col gap-6">
        <h1 className="text-2xl font-display font-medium text-white">Admin</h1>

        {/* Trading Toggle */}
        <section className="sr-panel p-6 flex flex-col gap-4">
          <h2 className="text-[11px] font-medium text-sr-neutral-300 uppercase tracking-wider">
            Trading Control
          </h2>
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <span className="text-sm text-white font-medium">Trading Enabled</span>
              <span className="text-xs text-sr-neutral-400">
                {tradingEnabled
                  ? "Users can place orders on the exchange."
                  : "Exchange is locked — users see the deposit/waiting screen."}
              </span>
            </div>
            <button
              onClick={handleToggle}
              disabled={toggling}
              className={`relative h-7 w-12 rounded-full transition-colors disabled:opacity-50 ${
                tradingEnabled ? "bg-sr-mint" : "bg-sr-neutral-700"
              }`}
            >
              <div
                className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${
                  tradingEnabled ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>
        </section>

        {/* Fee Balances */}
        <section className="sr-panel p-6 flex flex-col gap-4">
          <h2 className="text-[11px] font-medium text-sr-neutral-300 uppercase tracking-wider">
            Collected Fees
          </h2>
          {feeBalances.length === 0 ? (
            <span className="text-sm text-sr-neutral-500">No fees collected yet.</span>
          ) : (
            <div className="flex flex-col gap-3">
              {feeBalances.map((b: any) => (
                <div key={b.label} className="flex items-center justify-between">
                  <span className="text-sm text-white">{b.label}</span>
                  <span className="text-sm font-medium text-white tabular-nums font-display">
                    {b.formatted}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* System Stats */}
        <section className="sr-panel p-6 flex flex-col gap-4">
          <h2 className="text-[11px] font-medium text-sr-neutral-300 uppercase tracking-wider">
            System Stats
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-[11px] text-sr-neutral-400">TPS</span>
              <span className="text-lg font-display font-medium text-white tabular-nums">{tps}</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[11px] text-sr-neutral-400">Total Events</span>
              <span className="text-lg font-display font-medium text-white tabular-nums">
                {totalEvents.toLocaleString()}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[11px] text-sr-neutral-400">Accounts</span>
              <span className="text-lg font-display font-medium text-white tabular-nums">
                {accounts.length}
              </span>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};
